import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { Subject } from 'rxjs';
import { switchMap, takeUntil } from 'rxjs/operators';
import { NotificationService } from '~app/core/core.module';
import { TableCanDeactivate } from '~app/core/utils/can-deactivate/table-can-deactivate';
import { TableDataSource } from '~core/utils/material-table/table-data-source';
import { MetadataSetService } from '../../../core/data/metadata-set.service';
import { MetadataService } from '../../../core/data/metadata.service';
import { Application } from '../../../core/models/application.model';
import { MetadataSet } from '../../../core/models/metadata-set.model';
import { Metadata } from '../../../core/models/metadata.model';
import { ApplicationService } from '../application.service';
import { DialogSetDeleteComponent } from './dialog-set-delete/dialog-set-delete.component';
import { DialogSetPromptComponent } from './dialog-set-prompt/dialog-set-prompt.component';

@Component({
  selector: 'ngx-metadata-set-tab',
  templateUrl: './metadata-set-tab.component.html',
  styleUrls: ['./metadata-set-tab.component.scss']
})
export class MetadataSetsComponent extends TableCanDeactivate
  implements OnInit, OnDestroy {
  private unsubscribe$ = new Subject<void>();

  @Input() application: Application;
  metadataSets: MetadataSet[];
  selectedSet: MetadataSet;
  metadata: Metadata[];

  displayedColumns: string[] = [
    'definitionName',
    'definitionType',
    'default',
    'value',
    'actionsColumn'
  ];
  dataSource: TableDataSource<Metadata>;

  activeEdits = 0;
  @Output() activeEdits$ = new EventEmitter<number>();

  constructor(
    private _applicationService: ApplicationService,
    private _route: ActivatedRoute,
    private metadatasetService: MetadataSetService,
    private metadataService: MetadataService,
    private dialog: MatDialog,
    private _notificationService: NotificationService
  ) {
    super();
    this._route.parent.paramMap
      .pipe(
        takeUntil(this.unsubscribe$),
        switchMap((params: ParamMap) =>
          this._applicationService.getApp(+params.get('id'))
        )
      )
      .subscribe(app => {
        if (app) {
          this.application = app;
          this.metadatasetService
            .list(this.application.applicationId)
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(data => {
              this.metadataSets = data;
            });
        }
      });

    this.activeEdits$.emit(this.activeEdits);
  }

  ngOnInit() {}

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  onChangeSet(event) {
    this.metadataService
      .list(this.application.applicationId, this.selectedSet.metadataSetId)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(data => {
        data = data.sort((a, b) =>
          a.definitionName > b.definitionName
            ? 1
            : b.definitionName > a.definitionName
            ? -1
            : 0
        );
        this.metadata = data;
        this.dataSource = new TableDataSource(this.metadata);
        this.activeEdits = 0;
        this.activeEdits$.emit(this.activeEdits);
      });
  }

  startEditConfirm(row) {
    row.startEdit();
    this.activeEdits++;
    this.activeEdits$.emit(this.activeEdits);
  }

  createOrEditConfirm(row) {
    this.metadataService
      .edit(this.application.applicationId, row.currentData)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        row.confirmEditCreate();
        this.activeEdits--;
        this.activeEdits$.emit(this.activeEdits);
      });
  }

  cancelOrDeleteConfirm(row) {
    if (row.editing) {
      // Edit cancel
      row.cancelOrDelete();
      this.activeEdits--;
    }
    this.activeEdits$.emit(this.activeEdits);
  }

  addNew() {
    const dialogRef = this.dialog.open(DialogSetPromptComponent, {
      width: '300px'
    });

    dialogRef.afterClosed().subscribe(name => {
      if (name) {
        this.metadatasetService
          .edit(
            this.application.applicationId,
            new MetadataSet(
              0,
              this.application.applicationId,
              name,
              name,
              '',
              new Date(),
              1
            )
          )
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe(data => {
            this.metadataSets.push(data);
          });
      }
    });
  }

  deleteSelected() {
    const dialogRef = this.dialog.open(DialogSetDeleteComponent, {
      width: '300px',
      data: this.selectedSet.name
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.metadatasetService
          .delete(
            this.application.applicationId,
            this.selectedSet.metadataSetId
          )
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe(() => {
            this.activeEdits = 0;
            this.activeEdits$.emit(this.activeEdits);
            this.selectedSet = null;
            this.metadata = [];
            this.dataSource = new TableDataSource(this.metadata, Metadata);
            this.metadatasetService
              .list(this.application.applicationId)
              .subscribe(data => {
                this.metadataSets = data;
              });
          });
      }
    });
  }

  updateComments() {
    this.metadatasetService
      .edit(this.application.applicationId, this.selectedSet)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this._notificationService.success('Updated Metadata Set.');
      });
  }
}
